import React, { useEffect, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutGrid, FileText, Wallet, TrendingUp, SlidersHorizontal,
  Database, ShieldCheck, Users, Lock, Settings, Server, Puzzle,
  Rocket, ClipboardList, Siren, ChevronDown, ChevronsLeft, BadgeCheck,
  Mail, Building2, RefreshCcw, Palette, Wrench,
} from "lucide-react";
import { useOwnerShell } from "./OwnerShellContext";
import { useDeveloperMode } from "@/context/DeveloperModeContext";
import { useAttentionCenter, rankOf } from "@/hooks/useAttentionCenter";
import { isSnoozed } from "@/lib/attentionStore";
import Logo from "@/components/Logo";

// Sidebar taxonomy — Locked by PDR v2.1 §7.3.
// Do NOT rename or move items without amending the PDR first.
// Rebuilt in Recovery-2 (REC-01/02/03/04) to fully match PDR:
//   • CUSTOMERS group added (Organizations, Deep View is nested)
//   • Refunds & Disputes added under REVENUE
//   • Design Workspace surfaced under DEVELOPER (dev-mode toggle)
function buildNav({ devMode }) {
  const nav = [
    {
      id: "insight", label: "Insight",
      items: [
        { to: "/owner/attention",      label: "Attention Center", icon: Wrench,           accent: "warning" },
        { to: "/owner",                label: "Overview",         icon: LayoutGrid,       end: true },
        { to: "/owner/growth",         label: "Growth Analytics", icon: TrendingUp },
        { to: "/owner/digest",         label: "Weekly Digest",    icon: FileText },
      ],
    },
    {
      id: "revenue", label: "Revenue",
      items: [
        { to: "/owner/billing",           label: "Subscriptions",         icon: Wallet },
        { to: "/owner/billing/config",    label: "Billing Configuration", icon: SlidersHorizontal },
        { to: "/owner/billing/refunds",   label: "Refunds & Disputes",    icon: RefreshCcw },
      ],
    },
    {
      id: "customers", label: "Customers",
      items: [
        { to: "/owner/orgs", label: "Organizations", icon: Building2 },
      ],
    },
    {
      id: "trust", label: "Trust & Compliance",
      items: [
        { to: "/owner/identity", label: "Identity Verification", icon: BadgeCheck },
        { to: "/owner/security", label: "Security Events",       icon: Lock,          accent: "danger" },
        { to: "/owner/soc",      label: "SOC Actions",           icon: Siren,         accent: "danger" },
        { to: "/owner/audit",    label: "Audit Log",             icon: ClipboardList },
      ],
    },
    {
      id: "infrastructure", label: "Infrastructure",
      items: [
        { to: "/owner/production", label: "Environment",    icon: Server,   accent: "success" },
        { to: "/owner/providers",  label: "Providers",      icon: Puzzle,   accent: "warning" },
        { to: "/owner/storage",    label: "Storage",        icon: Database },
        { to: "/owner/launch",     label: "Launch Wizard",  icon: Rocket,   accent: "warning" },
        { to: "/owner/readiness",  label: "Readiness",      icon: ShieldCheck },
        { to: "/owner/staff",      label: "Platform Staff", icon: Users },
      ],
    },
    {
      id: "platform-settings", label: "Settings",
      items: [
        { to: "/owner/settings",           label: "Settings",          icon: Settings },
        { to: "/owner/privileged-emails",  label: "Privileged Emails", icon: Mail },
      ],
    },
  ];
  if (devMode) {
    nav.push({
      id: "developer", label: "Developer",
      items: [
        { to: "/owner/design",            label: "Design Workspace",  icon: Palette },
        { to: "/owner/design/compliance", label: "Design Compliance", icon: ClipboardList },
      ],
    });
  }
  return nav;
}

export default function OwnerSidebar() {
  const loc = useLocation();
  const { collapsed, groupsCollapsed, toggleCollapsed, toggleGroup, expandGroup } = useOwnerShell();
  const { devMode } = useDeveloperMode();
  const NAV = useMemo(() => buildNav({ devMode }), [devMode]);

  // Live counters — poll every 60s so the badge stays fresh without
  // spamming the aggregation endpoints. Failures are silent (badge stays
  // at last-known value or hidden). Zero new backend surface.
  //
  // IMPORTANT: the badge must match what the Attention Center page shows
  // by default ("Active only" filter — snoozed items are hidden). Otherwise
  // operators see a badge of 14 but only, say, 9 rows in the page and it
  // looks like the counter is lying.
  const { items } = useAttentionCenter({ pollMs: 60000 });
  const activeItems = items.filter((it) => !isSnoozed(it.id));
  const critical = activeItems.filter((i) => rankOf(i) === 0).length;
  const warning  = activeItems.filter((i) => rankOf(i) === 1).length;
  const badgeCount = critical + warning;
  const badgeTone  = critical > 0 ? "danger" : (warning > 0 ? "warning" : null);
  const badgeTitle = `${badgeCount} item${badgeCount === 1 ? "" : "s"} need your attention — ${critical} critical, ${warning} warning`;

  // Auto-expand the group that contains the active route.
  const activeGroupId = useMemo(() => {
    for (const g of NAV) {
      if (g.items.some(it => loc.pathname === it.to || loc.pathname.startsWith(it.to + "/"))) {
        return g.id;
      }
    }
    return null;
  }, [loc.pathname, NAV]);
  useEffect(() => {
    if (activeGroupId) expandGroup(activeGroupId);
  }, [activeGroupId, expandGroup]);

  return (
    <aside className="hbf-sidebar" data-testid="owner-sidebar" aria-label="Owner Console navigation">
      <div className="hbf-sidebar__brand">
        <div className="hbf-sidebar__brand-mark" aria-hidden>
          <Logo compact size={34} />
        </div>
        <div className="hbf-sidebar__brand-text">
          <div className="hbf-sidebar__brand-name">Hello by Flow</div>
          <div className="hbf-sidebar__brand-sub">Platform Owner</div>
        </div>
      </div>

      <div className="hbf-sidebar__nav">
        {NAV.map((group) => {
          const isGroupCollapsed = !!groupsCollapsed[group.id];
          return (
            <div
              key={group.id}
              className={`hbf-sidebar__group ${isGroupCollapsed ? "is-collapsed" : ""}`}
              data-testid={`owner-nav-group-${group.id}`}
            >
              <button
                type="button"
                className="hbf-sidebar__group-label"
                onClick={() => toggleGroup(group.id)}
                aria-expanded={!isGroupCollapsed}
                data-testid={`owner-nav-group-toggle-${group.id}`}
              >
                <span>{group.label}</span>
                <ChevronDown size={12} strokeWidth={2.4} className="hbf-sidebar__group-caret" />
              </button>
              <div className="hbf-sidebar__group-body">
                {group.items.map((it) => {
                  const Icon = it.icon;
                  const accent = it.accent ? `hbf-sidebar__item-accent-${it.accent}` : "";
                  const showBadge = it.to === "/owner/attention" && badgeCount > 0;
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      end={it.end}
                      data-tip={collapsed ? it.label : undefined}
                      data-testid={`owner-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) => `hbf-sidebar__item ${isActive ? "is-active" : ""}`}
                    >
                      <Icon className={`hbf-sidebar__icon ${accent}`} strokeWidth={1.75} />
                      <span className="hbf-sidebar__item-label">{it.label}</span>
                      {showBadge && (
                        <span
                          className={`hbf-sidebar__badge hbf-sidebar__badge--${badgeTone || "warning"}`}
                          data-testid="owner-nav-attention-badge"
                          title={badgeTitle}
                          aria-label={badgeTitle}
                        >
                          {badgeCount > 99 ? "99+" : badgeCount}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="hbf-sidebar__footer">
        <button
          type="button"
          className="hbf-sidebar__collapse-btn"
          onClick={toggleCollapsed}
          aria-pressed={collapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          data-testid="owner-sidebar-collapse"
        >
          <ChevronsLeft size={16} strokeWidth={2} />
          <span>{collapsed ? "Expand" : "Collapse"}</span>
        </button>
      </div>
    </aside>
  );
}
